/**
 * EmailComposer — pulls up a pre-filled, rich-default operator email
 * for a delivery / KDM / followup touchpoint. Operator does subtractive
 * editing: trim what doesn't apply, add personal touch, send.
 *
 * Why this exists: theaters often don't ask for the technical info they
 * need to ingest a DCP successfully. By pre-filling the operator's
 * email with checksums, extraction commands, KDM details, mix notes,
 * and a fallback-options list, we anticipate the venue's questions
 * before they ask. The operator's job becomes "remove what's not
 * relevant" instead of "remember and type everything."
 *
 * Surfaces:
 *   - DCP Library row: "Compose email" action item
 *   - DCP Distribution per-recipient card: same
 *   - Future: Venue page, KDM record actions
 *
 * Sending:
 *   - "Copy to clipboard" copies subject + body for pasting into Gmail
 *     etc. — works regardless of SMTP config.
 *   - "Open in mail client" uses mailto: with prefilled fields.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, TextField, MenuItem, Select,
  FormControl, InputLabel, Chip, Tooltip, Alert, Divider,
  ListSubheader, CircularProgress, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Email as MailIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from '../services/api';

const CATEGORY_LABEL = {
  delivery:        'Delivery',
  kdm:             'KDM',
  troubleshooting: 'Troubleshooting',
  followup:        'Followup',
  venue_request:   'Venue request',
};

// Order categories appear in the dropdown. Followup goes near the top
// since the composer is mostly opened for nudges/recovery in practice.
const CATEGORY_ORDER = ['delivery', 'kdm', 'followup', 'troubleshooting', 'venue_request'];

// Token names that the user usually doesn't want to type by hand —
// either because they're auto-filled from the User profile or because
// they're free-form fields that fit better as inline edits.
const HIDDEN_FILL_TOKENS = new Set(['recipient_first_name']);

function tokenLabel(tok) {
  // operator_phone → Operator phone
  return tok.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function EmailComposer({
  open,
  onClose,
  // Touchpoint binding — what context the system pulls auto-tokens from.
  // Examples:
  //   { touchpoint: 'dcp_delivery',   subjectId: <share.id> }
  //   { touchpoint: 'kdm_share',      subjectId: <kdm_record.id> }
  //   { touchpoint: 'venue_followup', subjectId: <venue.id> }
  touchpoint,
  subjectId,
  // Optional: friendly label for the dialog header.
  contextLabel,
  // Optional: tokens the operator wants substituted that the backend
  // can't infer (operator_phone, operator_location_or_distance).
  initialExtraTokens = {},
}) {
  const theme = useTheme();
  const [templates, setTemplates] = useState([]);
  const [pickedId, setPickedId] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [missing, setMissing] = useState([]);
  const [to, setTo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [renderTick, setRenderTick] = useState(0);
  const [error, setError] = useState(null);
  const [extraTokens, setExtraTokens] = useState(initialExtraTokens);
  const [copied, setCopied] = useState(false);
  // Only show the "missing tokens" panel once it's been computed at
  // least once — avoids a flash of "no missing tokens" before render.
  const [hasRendered, setHasRendered] = useState(false);

  // Reset when opened with a fresh context. Without this, switching
  // from one share to another reuses the prior body until the render
  // call returns, which looks like a stale composer.
  useEffect(() => {
    if (!open) return;
    setSubject(''); setBody(''); setMissing([]); setTo([]);
    setExtraTokens(initialExtraTokens || {});
    setHasRendered(false);
    setError(null);
    setCopied(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, touchpoint, subjectId]);

  // Fetch the templates relevant to this touchpoint when the dialog opens.
  useEffect(() => {
    if (!open || !touchpoint) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/api/email-templates`, { params: { touchpoint } })
      .then((r) => {
        if (cancelled) return;
        const list = r.data || [];
        setTemplates(list);
        if (list.length > 0) setPickedId(list[0].id);
      })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, touchpoint]);

  // Render the picked template against the current subject_id + extra
  // tokens. Re-runs when the operator picks a different template, edits
  // a token field, or hits Refresh (which bumps renderTick).
  useEffect(() => {
    if (!pickedId || !subjectId || !touchpoint) return undefined;
    let cancelled = false;
    setLoading(true);
    api.post(`/api/email-templates/render?template_id=${pickedId}`, {
      touchpoint, subject_id: subjectId, extra_tokens: extraTokens,
    })
      .then((r) => {
        if (cancelled) return;
        setSubject(r.data.subject);
        setBody(r.data.body);
        setMissing(r.data.missing_tokens || []);
        setTo(r.data.to || []);
        setHasRendered(true);
      })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pickedId, subjectId, touchpoint, extraTokens, renderTick]);

  const picked = useMemo(
    () => templates.find((t) => t.id === pickedId) || null,
    [templates, pickedId],
  );

  const handleCopy = async () => {
    const out = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(out);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      setError('Could not copy — clipboard blocked. Select and Cmd-C / Ctrl-C.');
    }
  };

  const handleMailto = () => {
    // mailto: doesn't reliably handle long bodies (browsers cap around
    // 2 KB). For typical templates this is fine; warn the operator if
    // we're over.
    const url = `mailto:${encodeURIComponent((to || []).join(','))}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    if (url.length > 8192) {
      setError("This email is too long for mailto: — use 'Copy to clipboard' and paste into Gmail.");
      return;
    }
    window.open(url, '_blank');
  };

  // Group templates by category. Displayed in CATEGORY_ORDER so the
  // followup section sits near the top — that's the most-common reason
  // operators open this composer.
  const ordered = useMemo(() => {
    const groups = {};
    templates.forEach((t) => {
      (groups[t.category] || (groups[t.category] = [])).push(t);
    });
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
      if (groups[cat]) ordered.push([cat, groups[cat]]);
    }
    // Anything not in CATEGORY_ORDER falls in at the end alphabetically.
    Object.keys(groups).sort().forEach((cat) => {
      if (!CATEGORY_ORDER.includes(cat)) ordered.push([cat, groups[cat]]);
    });
    return ordered;
  }, [templates]);

  const fillableMissing = (missing || []).filter((t) => !HIDDEN_FILL_TOKENS.has(t) && !t.startsWith('<format error'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { bgcolor: 'background.paper', height: { xs: '100%', sm: 'min(92vh, 920px)' } } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600 }}>Compose email</Typography>
          {contextLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contextLabel}
            </Typography>
          )}
        </Box>
        {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ─────────────── Template picker row ─────────────── */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Template</InputLabel>
              <Select
                value={pickedId || ''}
                label="Template"
                onChange={(e) => setPickedId(e.target.value)}
                renderValue={(val) => {
                  const t = templates.find((x) => x.id === val);
                  if (!t) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={CATEGORY_LABEL[t.category] || t.category}
                        sx={{ height: 18, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.4 }}
                      />
                      <Typography variant="body2">{t.name}</Typography>
                      {t.is_company_override && (
                        <Chip size="small" label="customized" sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                  );
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 480 } } }}
              >
                {ordered.flatMap(([cat, items]) => [
                  <ListSubheader key={`hdr-${cat}`} sx={{ bgcolor: 'background.paper', lineHeight: '24px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary' }}>
                    {CATEGORY_LABEL[cat] || cat}
                  </ListSubheader>,
                  ...items.map((t) => (
                    <MenuItem key={t.id} value={t.id} sx={{ alignItems: 'flex-start', py: 0.75 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t.name}</Typography>
                          {t.is_company_override && (
                            <Chip size="small" label="customized" sx={{ height: 16, fontSize: '0.6rem' }} />
                          )}
                        </Box>
                        {t.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.35 }}>
                            {t.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
            <Tooltip title="Re-pull tokens (e.g. after editing the screen / venue)">
              <IconButton size="small" onClick={() => setRenderTick((n) => n + 1)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {picked?.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.4 }}>
              {picked.description}
            </Typography>
          )}
        </Box>

        {/* ─────────────── Missing tokens (compact) ─────────────── */}
        {hasRendered && fillableMissing.length > 0 && (
          <Box sx={{ px: 2.5, pb: 1.5, flexShrink: 0 }}>
            <Box sx={{
              p: 1.25,
              border: 1,
              borderColor: 'warning.main',
              borderRadius: 1,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,167,38,0.06)' : 'rgba(255,167,38,0.08)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <EditIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Fill in or remove from body
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1 }}>
                {fillableMissing.map((tok) => (
                  <TextField
                    key={tok}
                    size="small"
                    label={tokenLabel(tok)}
                    placeholder={`{${tok}}`}
                    value={extraTokens[tok] || ''}
                    onChange={(e) => setExtraTokens((prev) => ({ ...prev, [tok]: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        <Divider />

        {/* ─────────────── To / Subject (pinned) ─────────────── */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ minWidth: 48, pt: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem' }}>To</Typography>
            <TextField
              fullWidth
              size="small"
              variant="standard"
              value={(to || []).join(', ')}
              onChange={(e) => setTo(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              InputProps={{ disableUnderline: false }}
              sx={{ '& .MuiInput-input': { fontSize: '0.875rem', fontFamily: 'monospace' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ minWidth: 48, pt: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem' }}>Subject</Typography>
            <TextField
              fullWidth
              size="small"
              variant="standard"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ '& .MuiInput-input': { fontSize: '0.95rem', fontWeight: 500 } }}
            />
          </Box>
        </Box>

        {/* ─────────────── Body (own scrollable frame) ───────────────
            Wrapping the textarea in its own flex-1 / overflow:hidden
            container is the load-bearing trick: previously a 50-line
            template made the textarea grow to 50 rows, blowing past
            the dialog and overlaying the action bar. Now the textarea
            owns the remaining vertical space and scrolls internally,
            while the To/Subject/picker stay pinned above. */}
        <Box sx={{ px: 2.5, pb: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem' }}>
              Body
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {body.length.toLocaleString()} chars
              {missing.length > 0 && (
                <> · <Box component="span" sx={{ color: 'warning.main' }}>{missing.length} unfilled token{missing.length === 1 ? '' : 's'}</Box></>
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
              display: 'flex',
              overflow: 'hidden',
              '&:focus-within': { borderColor: 'primary.main' },
            }}
          >
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              spellCheck
              style={{
                flex: 1,
                width: '100%',
                resize: 'none',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: 'inherit',
                padding: '12px 14px',
                fontFamily: '"Menlo", "Monaco", "Courier New", monospace',
                fontSize: '0.82rem',
                lineHeight: 1.6,
                overflow: 'auto',
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, fontSize: '0.7rem' }}>
            Trim what doesn't apply, then copy or open in your mail client.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} size="small">Close</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={handleCopy}
          color={copied ? 'success' : 'primary'}
          disabled={!subject || !body}
        >
          {copied ? 'Copied!' : 'Copy to clipboard'}
        </Button>
        <Button
          variant="contained"
          startIcon={<MailIcon />}
          onClick={handleMailto}
          disabled={!subject || !body}
        >
          Open in mail client
        </Button>
      </DialogActions>
    </Dialog>
  );
}
